/* eslint-disable react/display-name, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useMemo } from 'react';
import { Text } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { CellProps, Column, HeaderProps, UseSortByColumnProps } from 'react-table';
import { QuestionnaireTemplateListResponse } from '../../questionnaireTemplate.types';
import ActionCell from '../tableCell/ActionCell';
import LastModifiedCell from '../tableCell/LastModifiedCell';
import TemplateNameCell from '../tableCell/TemplateNameCell';
import UsageCell from '../tableCell/UsageCell';

type SortableHeaderProps = HeaderProps<QuestionnaireTemplateListResponse> & {
  column: HeaderProps<QuestionnaireTemplateListResponse>['column'] & UseSortByColumnProps<QuestionnaireTemplateListResponse>;
};
export const useTemplateColumns = (
  onQuestionnaireTemplateModalOpen: (template: QuestionnaireTemplateListResponse) => void,
  sortDisabled?: boolean,
): Column<QuestionnaireTemplateListResponse>[] => {
  const { t } = useTranslation('administration');

  return useMemo(
    () => [
      {
        Header: ({ column }: SortableHeaderProps) => (
          <Text color={!sortDisabled && column.isSorted ? 'blue.600' : 'gray.700'} fontWeight="semibold">
            {t('questionnaireTemplate.columns.templateName')}
          </Text>
        ),
        accessor: 'name',
        width: 320,
        disableSortBy: sortDisabled,
        Cell: ({ row }: CellProps<QuestionnaireTemplateListResponse>) => (
          <TemplateNameCell row={row.original} onQuestionnaireTemplateModalOpen={onQuestionnaireTemplateModalOpen} />
        ),
      },
      {
        Header: ({ column }: SortableHeaderProps) => (
          <Text color={sortDisabled || column.isSorted ? 'blue.600' : 'gray.700'} fontWeight="semibold">
            {t('questionnaireTemplate.columns.usage')}
          </Text>
        ),
        accessor: 'positionCount',
        width: 120,
        disableSortBy: sortDisabled,
        Cell: ({ value }: CellProps<QuestionnaireTemplateListResponse, number>) => <UsageCell usage={value} />,
      },
      {
        Header: ({ column }: SortableHeaderProps) => (
          <Text color={!sortDisabled && column.isSorted ? 'blue.600' : 'gray.700'} fontWeight="semibold">
            {t('questionnaireTemplate.columns.lastModified')}
          </Text>
        ),
        accessor: 'updatedAt',
        width: 160,
        disableSortBy: sortDisabled,
        Cell: ({ value }: CellProps<QuestionnaireTemplateListResponse, string>) => <LastModifiedCell date={value} />,
      },
      {
        Header: () => (
          <Text color="gray.700" fontWeight="semibold">
            {t('questionnaireTemplate.columns.action')}
          </Text>
        ),
        id: 'action',
        width: 80,
        disableSortBy: true,
        Cell: ({ row }: CellProps<QuestionnaireTemplateListResponse>) => (
          <ActionCell templateId={row.original.id} isDefault={row.original.default} />
        ),
      },
    ],
    [t, onQuestionnaireTemplateModalOpen, sortDisabled],
  );
};
